import { Box, Container, HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { FaPatreon } from "react-icons/fa6";
import { LuPuzzle } from "react-icons/lu";

import {
  Integration,
  IntegrationId,
  IntegrationSchema,
  IntegrationStatus,
  IntegrationType,
  connectWorkspaceIntegration,
  disconnectWorkspaceIntegration,
} from "../api/integrations";
import { Button } from "../components/chakra/button";
import { SectionHeader } from "../components/section-header";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
  useWorkspace,
} from "../hooks";
import { SettingsLayout } from "../layouts/settings";
import { loadIntegrations } from "../store/slices/integrations";

export const IntegrationSettingsPage = () => {
  const { schema, integrations } = useAppSelector((s) => s.integrations);
  const connectedIntegrations = React.useMemo(() => {
    const res: Record<string, Integration> = {};
    for (const integration of integrations) {
      if (integration.status !== IntegrationStatus.Connected) continue;
      res[integration.kind] = integration;
    }
    return res;
  }, [integrations]);

  return (
    <SettingsLayout>
      <Container pt="8" maxW="4xl">
        <Stack>
          <SectionHeader
            title="Integrations"
            subtitle="Manage your integrations."
          />
          {schema.map((s) => (
            <IntegrationCard
              integration={connectedIntegrations[s.id]}
              key={s.id}
              schema={s}
            />
          ))}
        </Stack>
      </Container>
    </SettingsLayout>
  );
};

interface IntegrationCardProps {
  integration?: Integration;
  schema: IntegrationSchema;
}

const getIntegrationIcon = (integrationId: IntegrationId) => {
  switch (integrationId) {
    case IntegrationId.Patreon:
      return <FaPatreon />;
    default:
      return <LuPuzzle />;
  }
};

const IntegrationCard = ({ schema, integration }: IntegrationCardProps) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace()!;
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const onConnect = React.useCallback(async () => {
    withToaster(async () => {
      await withLoading(async () => {
        switch (schema.type) {
          case IntegrationType.Oauth:
            {
              const { "oauth-url": oauthURL } =
                await connectWorkspaceIntegration(workspace.id, schema.id);
              window.location.href = oauthURL!;
            }
            break;
        }
      });
    });
  }, [schema, withLoading, withToaster, workspace]);
  const onDisconnect = React.useCallback(async () => {
    withToaster(async () => {
      await withLoading(async () => {
        await disconnectWorkspaceIntegration(workspace.id, integration!.kind);
        dispatch(loadIntegrations({ workspace }));
      });
    });
  }, [dispatch, integration, withLoading, withToaster, workspace]);

  return (
    <Box
      textAlign="center"
      borderRadius="5px"
      borderWidth="1px"
      p="4"
      pt="8"
      w="2xs"
    >
      <Stack align="center" gap="4" w="full">
        <HStack fontSize="2xl">
          {getIntegrationIcon(schema.id)}
          <Text>{schema.label}</Text>
        </HStack>
        {integration !== undefined ? (
          <Button colorPalette="red" loading={isLoading} onClick={onDisconnect}>
            Disconnect
          </Button>
        ) : (
          <Button loading={isLoading} onClick={onConnect}>
            Connect
          </Button>
        )}
      </Stack>
    </Box>
  );
};
